import React, { useEffect, useState } from 'react';
import { Grid, Paper, OutlinedInput, InputLabel, Button, Box, Typography, IconButton, Collapse, Select, MenuItem, LinearProgress, Chip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import PageHeader from '../../../../../components/page-header';
import { dataGridLocaleConfig } from '../../../../../config/data-grid-locale';
import { usePublicAmendmentSearchController } from './context/public-amendment-search-controller';
import { usePublicAmendmentSearchState } from './context/public-amendment-search-context';
import PublicAmendmentAppService from '../../../../../application/platform/public/public-amendment-app-service';
import { IAmendmentPagedResponse } from '../../../../../model/sge/amendment/response/amendment-paged-response';
import { AMENDMENT_TYPES, AMENDMENT_STATUSES, getAmendmentStatus } from '../../../../../model/sge/amendment/amendment-enums';
import { usePublicClientState, usePublicPortalBasePath } from '../../../context/public-client-context';

export function PublicAmendmentSearchComponent() {
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [rows, setRows] = useState<IAmendmentPagedResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const { handleOnChange, handleSearch, handleClearFilters, navigateToDetail } = usePublicAmendmentSearchController();
    const { parameters, searchTrigger } = usePublicAmendmentSearchState();
    const { clientId } = usePublicClientState();
    const basePath = usePublicPortalBasePath();

    useEffect(() => {
        if (!clientId) return;
        setLoading(true);
        PublicAmendmentAppService.list(clientId, parameters).then((r) => setRows(r.items)).finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTrigger, clientId]);

    const columns: GridColDef[] = [
        { field: 'id', headerName: 'ID', width: 80, hide: true },
        { field: 'name', headerName: 'Nome', flex: 1, minWidth: 220 },
        { field: 'parlamentarName', headerName: 'Parlamentar', flex: 1, minWidth: 180, valueFormatter: (params) => params.value || '-' },
        { field: 'politicalPartyAbbreviation', headerName: 'Partido', width: 100, valueFormatter: (params) => params.value || '-' },
        { field: 'year', headerName: 'Exercício', width: 100 },
        {
            field: 'status', headerName: 'Status', width: 150,
            renderCell: (params) => {
                const statusInfo = getAmendmentStatus(params.value);
                return <Chip label={statusInfo?.label || '-'} color={statusInfo?.color || 'default'} size="small" />;
            }
        },
        { field: 'totalAmount', headerName: 'Valor Total', width: 160, type: 'number', valueFormatter: (params) => params.value != null ? `R$ ${Number(params.value).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : '' },
        {
            field: 'actions', headerName: 'Detalhes', width: 100, sortable: false, align: 'center', headerAlign: 'center',
            renderCell: (params) => (
                <IconButton size="small" onClick={() => navigateToDetail(params.row)} title="Ver detalhes">
                    <VisibilityIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <React.Fragment>
            <Grid item xs={12}>
                <PageHeader
                    title="Emendas Parlamentares"
                    subtitle="Consulte as emendas parlamentares cadastradas"
                    homeHref={basePath}
                    breadcrumbs={[{ label: 'Emendas' }]}
                />
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderRadius: 1, p: 1, ml: -1, mr: -1, mb: filtersOpen ? 2 : 0, '&:hover': { backgroundColor: 'action.hover' } }} onClick={() => setFiltersOpen(!filtersOpen)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FilterListIcon color="action" /><Typography variant="h6">Filtros</Typography></Box>
                        <IconButton size="small" sx={{ pointerEvents: 'none' }}>{filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</IconButton>
                    </Box>
                    <Collapse in={filtersOpen}>
                        <Grid container spacing={1.5}>
                            <Grid item xs={12} md={4}>
                                <InputLabel>Nome</InputLabel>
                                <OutlinedInput fullWidth name="input-search" value={parameters.search || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12} md={2}>
                                <InputLabel>Exercício</InputLabel>
                                <OutlinedInput fullWidth name="input-year" type="number" value={parameters.year || ''} onChange={handleOnChange} inputProps={{ min: 2000, max: new Date().getFullYear() + 1 }} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputLabel>Status</InputLabel>
                                <Select fullWidth name="select-status" value={parameters.status || ''} onChange={handleOnChange}>
                                    <MenuItem value=""><em>Todos</em></MenuItem>
                                    {AMENDMENT_STATUSES.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
                                </Select>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputLabel>Tipo</InputLabel>
                                <Select fullWidth name="select-type" value={parameters.type || ''} onChange={handleOnChange}>
                                    <MenuItem value=""><em>Todos</em></MenuItem>
                                    {AMENDMENT_TYPES.map((t) => <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>)}
                                </Select>
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputLabel>Valor Mínimo</InputLabel>
                                <OutlinedInput fullWidth name="input-min-value" type="number" value={parameters.minValue ?? ''} onChange={handleOnChange} inputProps={{ min: 0, step: 0.01 }} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputLabel>Valor Máximo</InputLabel>
                                <OutlinedInput fullWidth name="input-max-value" type="number" value={parameters.maxValue ?? ''} onChange={handleOnChange} inputProps={{ min: 0, step: 0.01 }} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputLabel>Criada de</InputLabel>
                                <OutlinedInput fullWidth name="input-start-date" type="date" value={parameters.startDate || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12} md={3}>
                                <InputLabel>Criada até</InputLabel>
                                <OutlinedInput fullWidth name="input-end-date" type="date" value={parameters.endDate || ''} onChange={handleOnChange} />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <Button variant="outlined" color="secondary" startIcon={<ClearIcon />} onClick={handleClearFilters}>Limpar</Button>
                                    <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>Consultar</Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Collapse>
                </Paper>
            </Grid>
            <Grid item xs={12}>
                <Paper sx={{ p: 2, minHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        loading={loading}
                        pageSize={20}
                        rowsPerPageOptions={[20, 50, 100]}
                        sx={{ flex: 1 }}
                        disableSelectionOnClick
                        onRowClick={(params) => navigateToDetail(params.row)}
                        components={{ LoadingOverlay: LinearProgress }}
                        localeText={dataGridLocaleConfig}
                    />
                </Paper>
            </Grid>
        </React.Fragment>
    );
}
